import React, { useState, useEffect } from 'react';
import { formatAddress, formatNumber } from '../utils/constants';
import WalletConnector from './WalletConnector';
import '../styles/WelcomePage.css';

const WelcomePage = ({ 
  referralId, 
  referralInfo, 
  selectedNetwork, 
  onWalletConnect, 
  onWalletDisconnect, 
  onWalletError, 
  onChainChanged 
}) => {
  const [showFeatures, setShowFeatures] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState('');

  // Get required chain ID based on selected network
  const getRequiredChainId = () => {
    return selectedNetwork === 'opBNB' ? 204 : 5611;
  };

  // Handle wallet connection
  const handleWalletConnect = async (account, provider, walletType) => {
    setIsConnected(true);
    setConnectedAccount(account);
    await onWalletConnect(account, provider, walletType);
  };

  // Handle wallet disconnection
  const handleWalletDisconnect = () => {
    setIsConnected(false);
    setConnectedAccount('');
    onWalletDisconnect();
  };

  // Navigate to investment section
  const handleInvestNow = () => {
    // This will be handled by the parent App component
    // For now, we'll dispatch a custom event that the parent can listen to
    window.dispatchEvent(new CustomEvent('navigateToStake'));
  };

  // Handle join now - either connect wallet or navigate to invest
  const handleJoinNow = () => {
    if (isConnected) {
      handleInvestNow();
    } else {
      const connectButton = document.querySelector('.wallet-connect-btn');
      if (connectButton) {
        connectButton.click();
      }
    }
  };

  return (
    <div className="welcome-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          {/* Logo */}
          <div className="welcome-logo">
            <div className="logo-container">
              <img src="/logo.png" alt="Trail Logo" className="welcome-logo-image" onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }} />
              <div className="welcome-logo-fallback" style={{ display: 'none' }}>
                <span className="logo-icon">🚀</span>
              </div>
            </div>
            <h1>Trail</h1>
            <p className="logo-subtitle">AI-Powered DeFi Staking Platform</p>
          </div>

          {/* Referral Info */}
          {referralId && (
            <div className="referral-welcome">
              {referralInfo ? (
                <div className="referral-card">
                  <div className="referral-header">
                    <div className="referral-icon">👥</div>
                    <div className="referral-text">
                      <h3>You're Invited to Join Trail!</h3>
                      <p className="referral-subtitle">
                        Referred by <span className="upline-highlight">#{referralInfo.id}</span>
                      </p>
                    </div>
                  </div>

                  <div className="referral-details">
                    <div className="upline-card">
                      <div className="upline-header">
                        <div className="upline-avatar">
                          <span className="avatar-icon">🌟</span>
                        </div>
                        <div className="upline-info">
                          <div className="upline-id">ID: #{referralInfo.id}</div>
                          <div className="upline-address">{formatAddress(referralInfo.wallet)}</div>
                        </div>
                      </div>
                      
                      <div className="upline-stats">
                        <div className="upline-stat">
                          <span className="stat-value">{referralInfo.directReferrals}</span>
                          <span className="stat-label">Active Team</span>
                        </div>
                        <div className="upline-stat">
                          <span className="stat-value">{formatNumber(referralInfo.totalStaked, 0)}</span>
                          <span className="stat-label">USDT Staked</span>
                        </div>
                      </div>

                      <div className="trust-indicators">
                        <div className="trust-item">
                          <span className="trust-icon">✅</span>
                          <span>Verified Staker</span>
                        </div>
                        <div className="trust-item">
                          <span className="trust-icon">🏆</span>
                          <span>Experienced Leader</span>
                        </div>
                      </div>
                    </div>

                    <div className="join-benefits">
                      <h4>🎁 Your Exclusive Benefits:</h4>
                      <ul>
                        <li>
                          <span className="benefit-icon">✨</span>
                          Get expert guidance from an experienced staker
                        </li>
                        <li>
                          <span className="benefit-icon">🤝</span>
                          Join a proven community of successful investors
                        </li>
                        <li>
                          <span className="benefit-icon">📈</span>
                          Learn advanced AI-powered staking strategies
                        </li>
                        <li>
                          <span className="benefit-icon">🏆</span>
                          Access to exclusive achievement rewards
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="referral-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading referrer information...</p>
                </div>
              )}
            </div>
          )}

          {/* Main Action Section */}
          <div className="main-action-section">
            {!isConnected ? (
              <div className="wallet-connect-section">
                <div className="connect-container">
                  <h2>Connect Your Wallet to Begin</h2>
                  <p>Access AI-powered staking opportunities on opBNB</p>
                  
                  <div className="wallet-connect-wrapper">
                    <WalletConnector
                      onConnect={handleWalletConnect}
                      onDisconnect={handleWalletDisconnect}
                      onError={onWalletError}
                      onChainChanged={onChainChanged}
                      requiredChainId={getRequiredChainId()}
                      buttonClassName="welcome-connect-btn"
                    />
                  </div>

                  <div className="network-info">
                    <div className="network-badge">
                      <span className="network-dot"></span>
                      {selectedNetwork === 'opBNB' ? 'opBNB Mainnet' : 'opBNB Testnet'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="connected-section">
                <div className="connected-container">
                  <div className="connection-status">
                    <div className="status-icon">✅</div>
                    <h2>Wallet Connected Successfully!</h2>
                    <p className="connected-address">{formatAddress(connectedAccount)}</p>
                  </div>

                  <div className="action-buttons">
                    <button className="invest-now-btn primary" onClick={handleInvestNow}>
                      <span className="btn-icon">💰</span>
                      Start Investing Now
                    </button>
                    
                    <button 
                      className="disconnect-btn secondary" 
                      onClick={handleWalletDisconnect}
                    >
                      <span className="btn-icon">🔌</span>
                      Disconnect Wallet
                    </button>
                  </div>

                  <div className="quick-stats">
                    <div className="quick-stat">
                      <span className="quick-stat-label">Min Investment:</span>
                      <span className="quick-stat-value">100 USDT</span>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-label">Plans Available:</span>
                      <span className="quick-stat-value">10 AI Plans</span>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-label">Max ROI:</span>
                      <span className="quick-stat-value">5% Monthly</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Features Toggle */}
          <div className="features-toggle">
            <button 
              className="toggle-btn"
              onClick={() => setShowFeatures(!showFeatures)}
            >
              <span>Explore Trail's AI Features</span>
              <span className={`toggle-arrow ${showFeatures ? 'expanded' : ''}`}>▼</span>
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      {showFeatures && (
        <div className="features-section">
          <div className="features-container">
            <h3>🌟 AI-Powered Platform Features</h3>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🤖</div>
                <h4>AI-Optimized Plans</h4>
                <p>Smart algorithms analyze market conditions to optimize your staking returns across 10 different investment plans.</p>
                <div className="feature-highlight">Up to 5% Monthly ROI</div>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🧠</div>
                <h4>Intelligent Referrals</h4>
                <p>AI-powered referral system tracks and optimizes your network growth with smart commission distribution.</p>
                <div className="feature-highlight">Up to 6% Referral Bonus</div>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🏆</div>
                <h4>Dynamic Achievements</h4>
                <p>Progress through AI-calculated achievement levels from Cluster to Crown based on performance metrics.</p>
                <div className="feature-highlight">9 AI Achievement Tiers</div>
              </div>

              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h4>Smart Withdrawals</h4>
                <p>Flexible withdrawal options powered by AI timing algorithms for optimal return on investment.</p>
                <div className="feature-highlight">AI-Timed Options</div>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🛡️</div>
                <h4>Quantum Security</h4>
                <p>Advanced AI security protocols on opBNB blockchain ensure maximum protection of your investments.</p>
                <div className="feature-highlight">Military-Grade Security</div>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h4>Predictive Analytics</h4>
                <p>Real-time AI analytics predict market trends and optimize your staking strategy automatically.</p>
                <div className="feature-highlight">Live AI Predictions</div>
              </div>
            </div>

            <div className="investment-ranges">
              <h4>💎 AI Investment Matrix</h4>
              <div className="ranges-grid">
                <div className="range-item">
                  <span className="range-label">Minimum Entry:</span>
                  <span className="range-value">100 USDT</span>
                </div>
                <div className="range-item">
                  <span className="range-label">Maximum Investment:</span>
                  <span className="range-value">25,000 USDT</span>
                </div>
                <div className="range-item">
                  <span className="range-label">AI Plan Options:</span>
                  <span className="range-value">10 Smart Plans</span>
                </div>
                <div className="range-item">
                  <span className="range-label">Blockchain:</span>
                  <span className="range-value">opBNB Network</span>
                </div>
              </div>
            </div>

            <div className="cta-footer">
              <p>Ready to experience AI-powered staking?</p>
              {!isConnected ? (
                <div className="wallet-connect-wrapper">
                  <WalletConnector
                    onConnect={handleWalletConnect}
                    onDisconnect={handleWalletDisconnect}
                    onError={onWalletError}
                    onChainChanged={onChainChanged}
                    requiredChainId={getRequiredChainId()}
                    buttonClassName="welcome-connect-btn secondary"
                  />
                </div>
              ) : (
                <button className="invest-now-btn primary" onClick={handleInvestNow}>
                  <span className="btn-icon">🚀</span>
                  Start Your Investment Journey
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Info */}
      <div className="bottom-info">
        <div className="supported-wallets">
          <p>Supported AI-Compatible Wallets:</p>
          <div className="wallet-icons">
            <span className="wallet-item">MetaMask</span>
            <span className="wallet-item">Trust Wallet</span>
            <span className="wallet-item">WalletConnect</span>
            <span className="wallet-item">Binance Wallet</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;